import { useState } from 'react';
import { BookOpen, Coins, Gavel, ListChecks, PlayCircle, ShoppingBag, Volume2 } from 'lucide-react';
import type { ViewKey } from '../components/AppShell';
import { PageHeader, Panel } from '../components/ui';

type TutorialKey = 'commissions' | 'market-listing' | 'cash-exchange' | 'market' | 'auctions';

const tutorials: Array<{
  key: TutorialKey;
  title: string;
  eyebrow: string;
  description: string;
  video: string;
  poster: string;
  duration: string;
  icon: typeof ListChecks;
  target: ViewKey;
  steps: Array<{ title: string; body: string }>;
}> = [
  {
    key: 'commissions',
    title: '委托大厅使用教程',
    eyebrow: 'COMMISSION GUIDE',
    description: '学习发布委托、冻结报酬、承接委托和完成后提交。',
    video: '/tutorials/commissions.webm',
    poster: '/tutorials/commissions-poster.webp',
    duration: '约 25 秒 · 有配音',
    icon: ListChecks,
    target: 'commissions',
    steps: [
      { title: '发布委托', body: '写清标题、类型、交付要求和截止时间；发布时报酬会立即冻结。' },
      { title: '承接委托', body: '展开待接单的卡片，确认自己能够完成后点击“承接委托”。' },
      { title: '完成与确认', body: '完成后提交，发布者确认付款后，托管报酬自动进入接单者余额。' }
    ]
  },
  {
    key: 'market-listing',
    title: '玩家商店上架教程',
    eyebrow: 'LISTING GUIDE',
    description: '学习如何在游戏聊天栏打开商店菜单，把背包物品上架到玩家商店。',
    video: '/tutorials/market-listing.webm',
    poster: '/tutorials/market-listing-poster.webp',
    duration: '约 22 秒 · 有配音',
    icon: ShoppingBag,
    target: 'center',
    steps: [
      { title: '绑定角色', body: '先在网站绑定游戏角色，否则插件无法确认物品和网站账号的对应关系。' },
      { title: '打开商店菜单', body: '在游戏聊天栏发送“商店”，输入 1 选择“上架物品”。' },
      { title: '选择物品与数量', body: '输入背包中可上架物品的序号，再填写出售数量。' },
      { title: '填写价格并确认', body: '输入整数总价，最后发送 y 确认；物品扣除后即出现在网站玩家商店。' }
    ]
  },
  {
    key: 'cash-exchange',
    title: '物品回收与一键回收教程',
    eyebrow: 'RECYCLE GUIDE',
    description: '学习一键回收背包内全部可回收物品，以及分页选择单个物品并自定义回收数量。',
    video: '/tutorials/cash-exchange.webm',
    poster: '/tutorials/cash-exchange-poster.webp',
    duration: '约 20 秒 · 有配音',
    icon: Coins,
    target: 'center',
    steps: [
      { title: '打开回收菜单', body: '在游戏聊天栏发送“兑换金币”，第 1 项为一键回收，后面是按单价显示的物品列表。' },
      { title: '一键回收全部', body: '输入 1，系统自动识别背包内全部可回收物品并显示种类、数量和预计奖励。' },
      { title: '选择单个物品', body: '使用 n / p 翻页；物品序号从 2 开始，输入序号后再填写实际回收数量。' },
      { title: '确认回收', body: '核对预计奖励后输入 y 确认；单项会合并为一条记录，一键回收按物品种类分别记录。' }
    ]
  },
  {
    key: 'market',
    title: '市场购买与收货教程',
    eyebrow: 'MARKET GUIDE',
    description: '学习查看商品、购买、资金托管和订单确认。',
    video: '/tutorials/market.webm',
    poster: '/tutorials/market-poster.webp',
    duration: '约 21 秒 · 有配音',
    icon: ShoppingBag,
    target: 'center',
    steps: [
      { title: '选择商品', body: '在玩家商店核对商品说明、卖家、库存和单价。' },
      { title: '确认购买', body: '检查数量和总金额；资金会先进入系统托管，不会立即给卖家。' },
      { title: '确认收货', body: '收到商品并核对无误后再确认收货；有问题可以先发起上诉。' }
    ]
  },
  {
    key: 'auctions',
    title: '法拍场使用教程',
    eyebrow: 'AUCTION GUIDE',
    description: '学习拍卖权限、查看拍品、出价和成交结算。',
    video: '/tutorials/auction.webm',
    poster: '/tutorials/auction-poster.webp',
    duration: '约 21 秒 · 有配音',
    icon: Gavel,
    target: 'auctions',
    steps: [
      { title: '申请权限', body: '首次使用先提交拍卖申请，管理员审核通过后即可发起拍卖和出价。' },
      { title: '确认拍品', body: '核对当前价格、最低加价、领先者和结束时间。' },
      { title: '出价与结算', body: '领先出价资金会冻结；成交后系统自动扣除手续费并结算给卖家。' }
    ]
  }
];

export function TutorialsPage({ onNavigate }: { onNavigate: (view: ViewKey) => void }) {
  const [activeKey, setActiveKey] = useState<TutorialKey>('commissions');
  const active = tutorials.find(item => item.key === activeKey)!;
  const ActiveIcon = active.icon;

  return (
    <>
      <PageHeader title="使用教程" description="用带中文配音的短视频快速了解委托、市场和拍卖的完整操作流程。" action={<span className="tutorial-voice-note"><Volume2 size={15} /> 全部含中文配音</span>} />
      <Panel className="tutorial-intro" eyebrow="USER GUIDE">
        <div className="tutorial-intro-copy">
          <BookOpen size={24} />
          <div><strong>先看视频，再开始操作</strong><p>视频包含中文旁白，演示玩家端标准流程，不包含管理员操作。玩家商店上架和物品批量回收都有独立视频。</p></div>
        </div>
      </Panel>

      <div className="tutorial-switcher">
        {tutorials.map(item => {
          const Icon = item.icon;
          return (
            <button key={item.key} type="button" className={activeKey === item.key ? 'active' : ''} onClick={() => setActiveKey(item.key)}>
              <Icon size={20} />
              <span><strong>{item.title.replace('使用教程', '')}</strong><small>{item.duration}</small></span>
            </button>
          );
        })}
      </div>

      <section className="tutorial-player-layout">
        <div className="tutorial-video-column">
          <div className="tutorial-video-head">
            <div><span className="eyebrow">{active.eyebrow}</span><h2>{active.title}</h2><p>{active.description}</p></div>
            <ActiveIcon size={28} />
          </div>
          <div className="tutorial-video-frame">
            <video key={active.key} controls playsInline preload="metadata" poster={active.poster}>
              <source src={active.video} type="video/webm" />
              当前浏览器不支持 WebM 视频播放，请使用最新版 Chrome 或安卓 App。
            </video>
          </div>
          <div className="tutorial-video-links">
            <button type="button" className="button primary" onClick={() => onNavigate(active.target)}><PlayCircle size={16} /> 打开对应功能</button>
            <a className="button ghost" href={active.video} target="_blank" rel="noreferrer">单独打开视频</a>
          </div>
        </div>

        <aside className="tutorial-steps">
          <header><span className="eyebrow">STEP BY STEP</span><h2>本节要点</h2></header>
          {active.steps.map((step, index) => (
            <article key={step.title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div><strong>{step.title}</strong><p>{step.body}</p></div>
            </article>
          ))}
          <div className="tutorial-note">视频使用测试数据录制，实际页面中的金额、玩家名和商品内容会与你的站点不同。</div>
        </aside>
      </section>
    </>
  );
}
